import { Alert, Button, Grid, Paper, Typography } from "@mui/material";
import { City } from "@syadem/ariane-js";
import { Formik } from "formik";
import { ReactNode, useMemo, useState } from "react";
import { useAllOrganizations } from "../../../store";
import { TranslateFunction, createTranslateFn } from "../../../utils/formUtils";
import yup from "../../../utils/yup";
import { CitiesAutocomplete } from "../../components/CitiesAutocomplete";
import { StyledInput } from "../../components/mui/StyledInput";
import { useAppContext } from "../../hooks/useAppContext";
import { useCountryConfig } from "../../hooks/useCountryConfig";
import { useI18n } from "../../hooks/useI18n";
import { useServiceBus } from "../../hooks/useServiceBus";
import { PageLayout } from "../../layout/PageLayout";
import { theme } from "../../layout/Theme";

// TODO: Unused, replaced with NewTeamDialog
// Keeping it for now just in case
export function NewTeam() {
  const { t } = useI18n();
  const { organizationId } = useAppContext();
  const translateErrors: TranslateFunction = useMemo(() => createTranslateFn(t), [t]);
  const [errorMessage, setErrorMessage] = useState<string | ReactNode | undefined>(undefined);
  const [teamCity, setTeamCity] = useState<City | undefined>(undefined);
  const [createSuccess, setCreateSuccess] = useState<boolean>(false);
  const serviceBus = useServiceBus();
  const organizations = useAllOrganizations();
  const { zipCode, phoneNumber } = useCountryConfig();

  const teamCreationSchema = yup.object({
    name: yup.string().max(50).required(),
    address: yup.string().required(),
    city: yup.string().required(),
    zipCode: yup.string().required().min(zipCode.length).max(zipCode.length).matches(zipCode.regex),
    phoneNumber: yup
      .string()
      .required()
      .min(phoneNumber.minLength)
      .max(phoneNumber.maxLength)
      .matches(phoneNumber.regex),
  });

  return (
    <PageLayout title={t("common.interface.newTeam")}>
      {!createSuccess && (
        <Alert severity="info" sx={{ maxWidth: "md", marginX: "auto", mb: 2 }}>
          {t("team.createPending.description")}
        </Alert>
      )}
      <Paper
        sx={{
          overflow: "hidden",
          backgroundColor: "background.paper",
          border: `solid 1px ${theme.palette.neutral[200]}`,
          maxWidth: "md",
          marginX: "auto",
        }}
        elevation={0}
      >
        {createSuccess && (
          <Grid container justifyContent="center" sx={{ padding: "20px" }}>
            <Typography variant="subtitle2" sx={{ textAlign: "center" }}>
              {t("team.createPending.success")}
            </Typography>
          </Grid>
        )}
        {!createSuccess && organizations && organizationId && (
          <Formik
            initialValues={{
              name: "",
              address: "",
              city: "",
              zipCode: "",
              phoneNumber: "",
            }}
            validationSchema={teamCreationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              if (organizationId) {
                setErrorMessage(undefined);
                try {
                  await serviceBus.dispatch({
                    type: "createTeam",
                    organizationId,
                    team: values,
                  });

                  setCreateSuccess(true);
                } catch (_e) {
                  setSubmitting(false);
                  setErrorMessage(t("common.alerts.alert_notification"));
                }
              }
            }}
          >
            {({ errors, getFieldProps, touched, handleSubmit, setFieldValue, setFieldTouched, isSubmitting }) => (
              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Grid container rowSpacing={2} columnSpacing={4} justifyContent="space-between" padding={4}>
                  {errorMessage && (
                    <Grid size={12}>
                      <Alert severity="error">{errorMessage}</Alert>
                    </Grid>
                  )}
                  <Grid size={12}>
                    <StyledInput
                      disabled
                      name="organization"
                      value={organizations[organizationId].name}
                      fullWidth
                      size="small"
                      label={t("organization.name")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <StyledInput
                      id="name"
                      fullWidth
                      size="small"
                      error={touched.name && !!errors.name}
                      errorMessage={errors.name}
                      touched={touched.name}
                      translateErrors={translateErrors}
                      label={t("team.name")}
                      placeholder={t("team.namePlaceholder")}
                      required
                      testId="name"
                      {...getFieldProps("name")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <StyledInput
                      id="phoneNumber"
                      fullWidth
                      size="small"
                      error={touched.phoneNumber && !!errors.phoneNumber}
                      errorMessage={errors.phoneNumber}
                      touched={touched.phoneNumber}
                      translateErrors={translateErrors}
                      label={t("team.phoneNumber")}
                      required
                      placeholder={phoneNumber.placeholder}
                      testId="phoneNumber"
                      {...getFieldProps("phoneNumber")}
                    />
                  </Grid>
                  <Grid size={12}>
                    <StyledInput
                      id="address"
                      fullWidth
                      size="small"
                      error={touched.address && !!errors.address}
                      errorMessage={errors.address}
                      touched={touched.address}
                      translateErrors={translateErrors}
                      label={t("team.address")}
                      required
                      testId="address"
                      {...getFieldProps("address")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <StyledInput
                      id="zipCode"
                      fullWidth
                      size="small"
                      error={touched.zipCode && !!errors.zipCode}
                      errorMessage={errors.zipCode}
                      touched={touched.zipCode}
                      translateErrors={translateErrors}
                      label={t("team.zipCode")}
                      required
                      placeholder={zipCode.placeholder}
                      testId="zipCode"
                      {...getFieldProps("zipCode")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <CitiesAutocomplete
                      label={t("team.city")}
                      error={touched.city && !!errors.city}
                      errorMessage={errors.city}
                      touched={touched.city}
                      translateErrors={translateErrors}
                      placeholder={t("team.city")}
                      required
                      cityCode={teamCity ? teamCity.code : undefined}
                      onChange={(city) => {
                        setFieldTouched("city", true);
                        if (!city) {
                          setFieldValue("city", "");
                          setTeamCity(undefined);
                        } else {
                          setFieldValue("city", city.name);
                          setTeamCity(city);
                        }
                      }}
                    />
                  </Grid>
                  <Grid container justifyContent="space-between" size={12} sx={{ marginTop: "30px" }}>
                    <Button
                      variant="contained"
                      disableElevation
                      type="submit"
                      loading={isSubmitting}
                      data-testid="submit"
                    >
                      {t("common.cta.add")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        )}
      </Paper>
    </PageLayout>
  );
}
